// Key-fact conflict detection — shared by the dashboard audit card and the
// DocView facts review queue so both surfaces flag (and un-flag) the same
// things.
//
// Two kinds of conflict:
//   minority — a type that should usually be single-valued (jurisdiction,
//              address, date) carries several values; the most-used value is
//              presumed right and each minority value is questioned.
//   variant  — two entity spellings normalize to the same name (punctuation
//              or spacing drift, e.g. "HandsOn Global Management, LLC" vs
//              "HandsOn Global Management LLC"); the most-used spelling is
//              suggested as canonical.
//
// A conflict is RESOLVED when the doc's factReviews map carries a ruling for
// its key — either 'replaced' (a targeted replace was published) or 'keep'
// (the user ruled both values intentional, e.g. filing date vs signing date).

const MINORITY_TYPES = new Set(['jurisdiction', 'address', 'date']);

export const factReviewKey = (fact) => `${fact.type}:${fact.value}`;

const normalizeEntity = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

export function computeFactConflicts(facts, factReviews) {
  const reviews = factReviews || {};
  const out = [];

  const byType = new Map();
  for (const f of facts || []) {
    if (!byType.has(f.type)) byType.set(f.type, []);
    byType.get(f.type).push(f);
  }

  for (const [type, list] of byType) {
    if (MINORITY_TYPES.has(type) && list.length > 1) {
      const dominant = [...list].sort((a, b) => b.count - a.count)[0];
      for (const f of list) {
        if (f === dominant) continue;
        out.push({ kind: 'minority', type, fact: f, suggested: dominant });
      }
    }
    if (type === 'entity') {
      const byNorm = new Map();
      for (const f of list) {
        const n = normalizeEntity(f.value);
        if (!byNorm.has(n)) byNorm.set(n, []);
        byNorm.get(n).push(f);
      }
      for (const group of byNorm.values()) {
        if (group.length < 2) continue;
        const dominant = [...group].sort((a, b) => b.count - a.count)[0];
        for (const f of group) {
          if (f === dominant) continue;
          out.push({ kind: 'variant', type, fact: f, suggested: dominant });
        }
      }
    }
  }

  for (const c of out) {
    c.key = factReviewKey(c.fact);
    c.resolution = reviews[c.key] || null;
  }
  return out;
}

// The explicit ask — what exactly the user is being requested to decide.
export function conflictQuestion(c) {
  const a = `“${c.fact.value}” (×${c.fact.count})`;
  const b = `“${c.suggested.value}” (×${c.suggested.count})`;
  if (c.kind === 'variant') {
    return `${a} and ${b} look like the same entity spelled two ways. Standardize on one spelling, or confirm they are genuinely different parties.`;
  }
  switch (c.type) {
    case 'jurisdiction':
      return `The draft references ${a} alongside the dominant ${b}. Is this a leftover from the source document that should be replaced, or are both jurisdictions intentional?`;
    case 'date':
      return `Two dates appear: ${a} and the more-used ${b}. Dates often differ on purpose (e.g. Articles filing date vs signing date) — replace one, or confirm both are correct.`;
    case 'address':
      return `Two address renderings appear: ${a} and the more-used ${b}. Same place written two ways, or genuinely different addresses?`;
    default:
      return `${a} conflicts with the dominant ${b}. Replace it, or confirm both are intentional.`;
  }
}
